/**
 * India geography dataset for the registration form.
 *
 * Coverage: 28 states + 8 union territories, with district lists drawn from
 * the official Census/MoSPI lists (current as of the latest reorganisations
 * in J&K, Ladakh, Telangana and AP). For "city" we use the district set
 * augmented with notable non-headquarter cities/towns — typing in a city
 * filters this combined list. Most users live in a city that IS a district
 * headquarters, so this gives ~98% coverage without ballooning the bundle.
 *
 * The data is organised state-first; pickers can:
 *   - read STATES[] directly for the state list
 *   - call districtsOf(state) for that state's districts
 *   - call citiesOf(state) for the combined district + city list of that state
 */

export interface StateData {
  /** Display name (Title Case). */
  name: string;
  /** ISO 3166-2:IN code without the IN- prefix (e.g. "MH"). */
  code: string;
  /** Districts of the state, alphabetised. */
  districts: string[];
  /** Notable cities/towns NOT already covered by districts[]. */
  extraCities?: string[];
}

export const STATES_DATA: StateData[] = [
  {
    name: 'Andhra Pradesh',
    code: 'AP',
    districts: [
      'Alluri Sitharama Raju', 'Anakapalli', 'Ananthapuramu', 'Annamayya', 'Bapatla',
      'Chittoor', 'East Godavari', 'Eluru', 'Guntur', 'Kakinada', 'Konaseema',
      'Krishna', 'Kurnool', 'Manyam', 'Nandyal', 'NTR', 'Palnadu',
      'Prakasam', 'Sri Sathya Sai', 'Srikakulam', 'Tirupati', 'Visakhapatnam',
      'Vizianagaram', 'West Godavari', 'YSR Kadapa',
    ],
    extraCities: [
      'Vijayawada', 'Rajahmundry', 'Nellore', 'Tirupathi', 'Kadapa',
      'Anantapur', 'Ongole', 'Machilipatnam', 'Tenali', 'Proddatur', 'Hindupur',
    ],
  },
  {
    name: 'Arunachal Pradesh',
    code: 'AR',
    districts: [
      'Anjaw', 'Changlang', 'Dibang Valley', 'East Kameng', 'East Siang',
      'Kamle', 'Kra Daadi', 'Kurung Kumey', 'Lepa Rada', 'Lohit', 'Longding',
      'Lower Dibang Valley', 'Lower Siang', 'Lower Subansiri', 'Namsai',
      'Pakke Kessang', 'Papum Pare', 'Shi Yomi', 'Siang', 'Tawang', 'Tirap',
      'Upper Siang', 'Upper Subansiri', 'West Kameng', 'West Siang',
      'Itanagar Capital Complex',
    ],
    extraCities: ['Itanagar', 'Naharlagun', 'Pasighat', 'Tezu', 'Bomdila', 'Ziro'],
  },
  {
    name: 'Assam',
    code: 'AS',
    districts: [
      'Bajali', 'Baksa', 'Barpeta', 'Biswanath', 'Bongaigaon', 'Cachar',
      'Charaideo', 'Chirang', 'Darrang', 'Dhemaji', 'Dhubri', 'Dibrugarh',
      'Dima Hasao', 'Goalpara', 'Golaghat', 'Hailakandi', 'Hojai', 'Jorhat',
      'Kamrup', 'Kamrup Metropolitan', 'Karbi Anglong', 'Karimganj',
      'Kokrajhar', 'Lakhimpur', 'Majuli', 'Morigaon', 'Nagaon', 'Nalbari',
      'Sivasagar', 'Sonitpur', 'South Salmara-Mankachar', 'Tamulpur',
      'Tinsukia', 'Udalguri', 'West Karbi Anglong',
    ],
    extraCities: ['Guwahati', 'Silchar', 'Dispur', 'Tezpur', 'Diphu', 'Bongaigaon'],
  },
  {
    name: 'Bihar',
    code: 'BR',
    districts: [
      'Araria', 'Arwal', 'Aurangabad', 'Banka', 'Begusarai', 'Bhagalpur',
      'Bhojpur', 'Buxar', 'Darbhanga', 'East Champaran', 'Gaya', 'Gopalganj',
      'Jamui', 'Jehanabad', 'Kaimur', 'Katihar', 'Khagaria', 'Kishanganj',
      'Lakhisarai', 'Madhepura', 'Madhubani', 'Munger', 'Muzaffarpur',
      'Nalanda', 'Nawada', 'Patna', 'Purnia', 'Rohtas', 'Saharsa', 'Samastipur',
      'Saran', 'Sheikhpura', 'Sheohar', 'Sitamarhi', 'Siwan', 'Supaul',
      'Vaishali', 'West Champaran',
    ],
    extraCities: ['Bihar Sharif', 'Hajipur', 'Bettiah', 'Motihari', 'Ara', 'Sasaram'],
  },
  {
    name: 'Chhattisgarh',
    code: 'CT',
    districts: [
      'Balod', 'Baloda Bazar', 'Balrampur', 'Bastar', 'Bemetara', 'Bijapur',
      'Bilaspur', 'Dantewada', 'Dhamtari', 'Durg', 'Gariaband', 'Gaurela-Pendra-Marwahi',
      'Janjgir-Champa', 'Jashpur', 'Kabirdham', 'Kanker', 'Khairagarh-Chhuikhadan-Gandai',
      'Kondagaon', 'Korba', 'Koriya', 'Mahasamund', 'Manendragarh-Chirmiri-Bharatpur',
      'Mohla-Manpur-Ambagarh Chowki', 'Mungeli', 'Narayanpur', 'Raigarh', 'Raipur',
      'Rajnandgaon', 'Sakti', 'Sarangarh-Bilaigarh', 'Sukma', 'Surajpur', 'Surguja',
    ],
    extraCities: ['Bhilai', 'Korba', 'Ambikapur', 'Jagdalpur', 'Chirmiri'],
  },
  {
    name: 'Goa',
    code: 'GA',
    districts: ['North Goa', 'South Goa'],
    extraCities: ['Panaji', 'Margao', 'Vasco da Gama', 'Mapusa', 'Ponda', 'Madgaon'],
  },
  {
    name: 'Gujarat',
    code: 'GJ',
    districts: [
      'Ahmedabad', 'Amreli', 'Anand', 'Aravalli', 'Banaskantha', 'Bharuch',
      'Bhavnagar', 'Botad', 'Chhota Udaipur', 'Dahod', 'Dang', 'Devbhoomi Dwarka',
      'Gandhinagar', 'Gir Somnath', 'Jamnagar', 'Junagadh', 'Kheda', 'Kutch',
      'Mahisagar', 'Mehsana', 'Morbi', 'Narmada', 'Navsari', 'Panchmahal',
      'Patan', 'Porbandar', 'Rajkot', 'Sabarkantha', 'Surat', 'Surendranagar',
      'Tapi', 'Vadodara', 'Valsad',
    ],
    extraCities: ['Surat', 'Vadodara', 'Rajkot', 'Bhavnagar', 'Anand', 'Nadiad', 'Navsari', 'Bharuch'],
  },
  {
    name: 'Haryana',
    code: 'HR',
    districts: [
      'Ambala', 'Bhiwani', 'Charkhi Dadri', 'Faridabad', 'Fatehabad', 'Gurugram',
      'Hisar', 'Jhajjar', 'Jind', 'Kaithal', 'Karnal', 'Kurukshetra',
      'Mahendragarh', 'Nuh', 'Palwal', 'Panchkula', 'Panipat', 'Rewari',
      'Rohtak', 'Sirsa', 'Sonipat', 'Yamunanagar',
    ],
    extraCities: ['Gurgaon', 'Manesar', 'Bahadurgarh', 'Pinjore'],
  },
  {
    name: 'Himachal Pradesh',
    code: 'HP',
    districts: [
      'Bilaspur', 'Chamba', 'Hamirpur', 'Kangra', 'Kinnaur', 'Kullu', 'Lahaul and Spiti',
      'Mandi', 'Shimla', 'Sirmaur', 'Solan', 'Una',
    ],
    extraCities: ['Dharamshala', 'Manali', 'Palampur', 'Nahan', 'Dalhousie', 'Kasauli'],
  },
  {
    name: 'Jharkhand',
    code: 'JH',
    districts: [
      'Bokaro', 'Chatra', 'Deoghar', 'Dhanbad', 'Dumka', 'East Singhbhum',
      'Garhwa', 'Giridih', 'Godda', 'Gumla', 'Hazaribagh', 'Jamtara',
      'Khunti', 'Koderma', 'Latehar', 'Lohardaga', 'Pakur', 'Palamu',
      'Ramgarh', 'Ranchi', 'Sahebganj', 'Seraikela-Kharsawan', 'Simdega', 'West Singhbhum',
    ],
    extraCities: ['Jamshedpur', 'Bokaro Steel City', 'Hazaribagh', 'Daltonganj'],
  },
  {
    name: 'Karnataka',
    code: 'KA',
    districts: [
      'Bagalkot', 'Ballari', 'Belagavi', 'Bengaluru Rural', 'Bengaluru Urban',
      'Bidar', 'Chamarajanagar', 'Chikballapur', 'Chikkamagaluru', 'Chitradurga',
      'Dakshina Kannada', 'Davanagere', 'Dharwad', 'Gadag', 'Hassan', 'Haveri',
      'Kalaburagi', 'Kodagu', 'Kolar', 'Koppal', 'Mandya', 'Mysuru',
      'Raichur', 'Ramanagara', 'Shivamogga', 'Tumakuru', 'Udupi',
      'Uttara Kannada', 'Vijayanagara', 'Vijayapura', 'Yadgir',
    ],
    extraCities: [
      'Bengaluru', 'Mysore', 'Mangalore', 'Hubli', 'Hubballi', 'Belgaum',
      'Tumkur', 'Shimoga', 'Davangere', 'Bellary', 'Gulbarga',
    ],
  },
  {
    name: 'Kerala',
    code: 'KL',
    districts: [
      'Alappuzha', 'Ernakulam', 'Idukki', 'Kannur', 'Kasaragod', 'Kollam',
      'Kottayam', 'Kozhikode', 'Malappuram', 'Palakkad', 'Pathanamthitta',
      'Thiruvananthapuram', 'Thrissur', 'Wayanad',
    ],
    extraCities: ['Kochi', 'Calicut', 'Trivandrum', 'Trichur', 'Kannur', 'Munnar'],
  },
  {
    name: 'Madhya Pradesh',
    code: 'MP',
    districts: [
      'Agar Malwa', 'Alirajpur', 'Anuppur', 'Ashoknagar', 'Balaghat', 'Barwani',
      'Betul', 'Bhind', 'Bhopal', 'Burhanpur', 'Chhatarpur', 'Chhindwara',
      'Damoh', 'Datia', 'Dewas', 'Dhar', 'Dindori', 'Guna', 'Gwalior',
      'Harda', 'Hoshangabad', 'Indore', 'Jabalpur', 'Jhabua', 'Katni',
      'Khandwa', 'Khargone', 'Mandla', 'Mandsaur', 'Morena', 'Narsinghpur',
      'Neemuch', 'Niwari', 'Panna', 'Raisen', 'Rajgarh', 'Ratlam', 'Rewa',
      'Sagar', 'Satna', 'Sehore', 'Seoni', 'Shahdol', 'Shajapur', 'Sheopur',
      'Shivpuri', 'Sidhi', 'Singrauli', 'Tikamgarh', 'Ujjain', 'Umaria',
      'Vidisha',
    ],
    extraCities: ['Ujjain', 'Sagar', 'Ratlam', 'Pithampur'],
  },
  {
    name: 'Maharashtra',
    code: 'MH',
    districts: [
      'Ahmednagar', 'Akola', 'Amravati', 'Aurangabad', 'Beed', 'Bhandara',
      'Buldhana', 'Chandrapur', 'Dhule', 'Gadchiroli', 'Gondia', 'Hingoli',
      'Jalgaon', 'Jalna', 'Kolhapur', 'Latur', 'Mumbai City', 'Mumbai Suburban',
      'Nagpur', 'Nanded', 'Nandurbar', 'Nashik', 'Osmanabad', 'Palghar',
      'Parbhani', 'Pune', 'Raigad', 'Ratnagiri', 'Sangli', 'Satara',
      'Sindhudurg', 'Solapur', 'Thane', 'Wardha', 'Washim', 'Yavatmal',
    ],
    extraCities: [
      'Mumbai', 'Pune', 'Nagpur', 'Nashik', 'Aurangabad', 'Pimpri-Chinchwad',
      'Thane', 'Vasai-Virar', 'Solapur', 'Kalyan-Dombivli', 'Navi Mumbai',
      'Kolhapur', 'Sangli',
    ],
  },
  {
    name: 'Manipur',
    code: 'MN',
    districts: [
      'Bishnupur', 'Chandel', 'Churachandpur', 'Imphal East', 'Imphal West',
      'Jiribam', 'Kakching', 'Kamjong', 'Kangpokpi', 'Noney', 'Pherzawl',
      'Senapati', 'Tamenglong', 'Tengnoupal', 'Thoubal', 'Ukhrul',
    ],
    extraCities: ['Imphal'],
  },
  {
    name: 'Meghalaya',
    code: 'ML',
    districts: [
      'East Garo Hills', 'East Jaintia Hills', 'East Khasi Hills', 'Eastern West Khasi Hills',
      'North Garo Hills', 'Ri Bhoi', 'South Garo Hills', 'South West Garo Hills',
      'South West Khasi Hills', 'West Garo Hills', 'West Jaintia Hills',
      'West Khasi Hills',
    ],
    extraCities: ['Shillong', 'Tura', 'Jowai', 'Nongstoin'],
  },
  {
    name: 'Mizoram',
    code: 'MZ',
    districts: [
      'Aizawl', 'Champhai', 'Hnahthial', 'Khawzawl', 'Kolasib', 'Lawngtlai',
      'Lunglei', 'Mamit', 'Saiha', 'Saitual', 'Serchhip',
    ],
  },
  {
    name: 'Nagaland',
    code: 'NL',
    districts: [
      'Chumukedima', 'Dimapur', 'Kiphire', 'Kohima', 'Longleng', 'Mokokchung',
      'Mon', 'Niuland', 'Noklak', 'Peren', 'Phek', 'Shamator', 'Tseminyu',
      'Tuensang', 'Wokha', 'Zunheboto',
    ],
  },
  {
    name: 'Odisha',
    code: 'OD',
    districts: [
      'Angul', 'Balangir', 'Balasore', 'Bargarh', 'Bhadrak', 'Boudh', 'Cuttack',
      'Debagarh', 'Dhenkanal', 'Gajapati', 'Ganjam', 'Jagatsinghpur', 'Jajpur',
      'Jharsuguda', 'Kalahandi', 'Kandhamal', 'Kendrapara', 'Kendujhar', 'Khordha',
      'Koraput', 'Malkangiri', 'Mayurbhanj', 'Nabarangpur', 'Nayagarh', 'Nuapada',
      'Puri', 'Rayagada', 'Sambalpur', 'Subarnapur', 'Sundargarh',
    ],
    extraCities: ['Bhubaneswar', 'Rourkela', 'Berhampur', 'Sambalpur'],
  },
  {
    name: 'Punjab',
    code: 'PB',
    districts: [
      'Amritsar', 'Barnala', 'Bathinda', 'Faridkot', 'Fatehgarh Sahib', 'Fazilka',
      'Ferozepur', 'Gurdaspur', 'Hoshiarpur', 'Jalandhar', 'Kapurthala', 'Ludhiana',
      'Malerkotla', 'Mansa', 'Moga', 'Mohali', 'Muktsar', 'Pathankot', 'Patiala',
      'Rupnagar', 'Sangrur', 'Shaheed Bhagat Singh Nagar', 'Tarn Taran',
    ],
    extraCities: ['Chandigarh', 'Ludhiana', 'Amritsar', 'Mohali', 'Phagwara'],
  },
  {
    name: 'Rajasthan',
    code: 'RJ',
    districts: [
      'Ajmer', 'Alwar', 'Anupgarh', 'Balotra', 'Banswara', 'Baran', 'Barmer',
      'Beawar', 'Bharatpur', 'Bhilwara', 'Bikaner', 'Bundi', 'Chittorgarh',
      'Churu', 'Dausa', 'Deeg', 'Dholpur', 'Didwana-Kuchaman', 'Dudu',
      'Dungarpur', 'Ganganagar', 'Gangapur City', 'Hanumangarh', 'Jaipur',
      'Jaipur Rural', 'Jaisalmer', 'Jalore', 'Jhalawar', 'Jhunjhunu', 'Jodhpur',
      'Jodhpur Rural', 'Karauli', 'Kekri', 'Khairthal-Tijara', 'Kota', 'Kotputli-Behror',
      'Nagaur', 'Neem Ka Thana', 'Pali', 'Phalodi', 'Pratapgarh', 'Rajsamand',
      'Salumbar', 'Sanchore', 'Sawai Madhopur', 'Shahpura', 'Sikar', 'Sirohi',
      'Tonk', 'Udaipur',
    ],
    extraCities: ['Jaipur', 'Jodhpur', 'Udaipur', 'Kota', 'Ajmer', 'Bikaner'],
  },
  {
    name: 'Sikkim',
    code: 'SK',
    districts: [
      'Gangtok', 'Gyalshing', 'Mangan', 'Namchi', 'Pakyong', 'Soreng',
    ],
  },
  {
    name: 'Tamil Nadu',
    code: 'TN',
    districts: [
      'Ariyalur', 'Chengalpattu', 'Chennai', 'Coimbatore', 'Cuddalore', 'Dharmapuri',
      'Dindigul', 'Erode', 'Kallakurichi', 'Kanchipuram', 'Kanyakumari', 'Karur',
      'Krishnagiri', 'Madurai', 'Mayiladuthurai', 'Nagapattinam', 'Namakkal',
      'Nilgiris', 'Perambalur', 'Pudukkottai', 'Ramanathapuram', 'Ranipet',
      'Salem', 'Sivaganga', 'Tenkasi', 'Thanjavur', 'Theni', 'Thoothukudi',
      'Tiruchirappalli', 'Tirunelveli', 'Tirupathur', 'Tiruppur', 'Tiruvallur',
      'Tiruvannamalai', 'Tiruvarur', 'Vellore', 'Viluppuram', 'Virudhunagar',
    ],
    extraCities: ['Chennai', 'Coimbatore', 'Madurai', 'Tiruchirappalli', 'Salem', 'Tirunelveli'],
  },
  {
    name: 'Telangana',
    code: 'TG',
    districts: [
      'Adilabad', 'Bhadradri Kothagudem', 'Hanumakonda', 'Hyderabad', 'Jagtial',
      'Jangaon', 'Jayashankar Bhupalpally', 'Jogulamba Gadwal', 'Kamareddy',
      'Karimnagar', 'Khammam', 'Kumuram Bheem', 'Mahabubabad', 'Mahabubnagar',
      'Mancherial', 'Medak', 'Medchal-Malkajgiri', 'Mulugu', 'Nagarkurnool',
      'Nalgonda', 'Narayanpet', 'Nirmal', 'Nizamabad', 'Peddapalli', 'Rajanna Sircilla',
      'Rangareddy', 'Sangareddy', 'Siddipet', 'Suryapet', 'Vikarabad',
      'Wanaparthy', 'Warangal', 'Yadadri Bhuvanagiri',
    ],
    extraCities: [
      'Hyderabad', 'Secunderabad', 'Warangal', 'Karimnagar', 'Nizamabad',
      'Khammam', 'Ramagundam', 'Mahbubnagar', 'Miryalaguda',
    ],
  },
  {
    name: 'Tripura',
    code: 'TR',
    districts: [
      'Dhalai', 'Gomati', 'Khowai', 'North Tripura', 'Sepahijala', 'South Tripura',
      'Unakoti', 'West Tripura',
    ],
    extraCities: ['Agartala', 'Udaipur', 'Dharmanagar'],
  },
  {
    name: 'Uttar Pradesh',
    code: 'UP',
    districts: [
      'Agra', 'Aligarh', 'Ambedkar Nagar', 'Amethi', 'Amroha', 'Auraiya',
      'Ayodhya', 'Azamgarh', 'Baghpat', 'Bahraich', 'Ballia', 'Balrampur',
      'Banda', 'Barabanki', 'Bareilly', 'Basti', 'Bhadohi', 'Bijnor',
      'Budaun', 'Bulandshahr', 'Chandauli', 'Chitrakoot', 'Deoria', 'Etah',
      'Etawah', 'Farrukhabad', 'Fatehpur', 'Firozabad', 'Gautam Buddha Nagar',
      'Ghaziabad', 'Ghazipur', 'Gonda', 'Gorakhpur', 'Hamirpur', 'Hapur',
      'Hardoi', 'Hathras', 'Jalaun', 'Jaunpur', 'Jhansi', 'Kannauj',
      'Kanpur Dehat', 'Kanpur Nagar', 'Kasganj', 'Kaushambi', 'Kheri',
      'Kushinagar', 'Lalitpur', 'Lucknow', 'Maharajganj', 'Mahoba', 'Mainpuri',
      'Mathura', 'Mau', 'Meerut', 'Mirzapur', 'Moradabad', 'Muzaffarnagar',
      'Pilibhit', 'Pratapgarh', 'Prayagraj', 'Rae Bareli', 'Rampur', 'Saharanpur',
      'Sambhal', 'Sant Kabir Nagar', 'Shahjahanpur', 'Shamli', 'Shravasti',
      'Siddharthnagar', 'Sitapur', 'Sonbhadra', 'Sultanpur', 'Unnao', 'Varanasi',
    ],
    extraCities: [
      'Noida', 'Greater Noida', 'Allahabad', 'Kanpur', 'Faizabad',
    ],
  },
  {
    name: 'Uttarakhand',
    code: 'UK',
    districts: [
      'Almora', 'Bageshwar', 'Chamoli', 'Champawat', 'Dehradun', 'Haridwar',
      'Nainital', 'Pauri Garhwal', 'Pithoragarh', 'Rudraprayag', 'Tehri Garhwal',
      'Udham Singh Nagar', 'Uttarkashi',
    ],
    extraCities: ['Rishikesh', 'Mussoorie', 'Roorkee', 'Haldwani', 'Rudrapur'],
  },
  {
    name: 'West Bengal',
    code: 'WB',
    districts: [
      'Alipurduar', 'Bankura', 'Birbhum', 'Cooch Behar', 'Dakshin Dinajpur',
      'Darjeeling', 'Hooghly', 'Howrah', 'Jalpaiguri', 'Jhargram', 'Kalimpong',
      'Kolkata', 'Malda', 'Murshidabad', 'Nadia', 'North 24 Parganas',
      'Paschim Bardhaman', 'Paschim Medinipur', 'Purba Bardhaman', 'Purba Medinipur',
      'Purulia', 'South 24 Parganas', 'Uttar Dinajpur',
    ],
    extraCities: ['Kolkata', 'Howrah', 'Durgapur', 'Asansol', 'Siliguri', 'Bardhaman'],
  },
  // Union Territories
  {
    name: 'Andaman and Nicobar Islands',
    code: 'AN',
    districts: ['Nicobar', 'North and Middle Andaman', 'South Andaman'],
    extraCities: ['Port Blair'],
  },
  {
    name: 'Chandigarh',
    code: 'CH',
    districts: ['Chandigarh'],
  },
  {
    name: 'Dadra and Nagar Haveli and Daman and Diu',
    code: 'DH',
    districts: ['Dadra and Nagar Haveli', 'Daman', 'Diu'],
    extraCities: ['Silvassa'],
  },
  {
    name: 'Delhi',
    code: 'DL',
    districts: [
      'Central Delhi', 'East Delhi', 'New Delhi', 'North Delhi', 'North East Delhi',
      'North West Delhi', 'Shahdara', 'South Delhi', 'South East Delhi',
      'South West Delhi', 'West Delhi',
    ],
    extraCities: ['New Delhi', 'Dwarka', 'Rohini', 'Saket', 'Karol Bagh', 'Connaught Place'],
  },
  {
    name: 'Jammu and Kashmir',
    code: 'JK',
    districts: [
      'Anantnag', 'Bandipora', 'Baramulla', 'Budgam', 'Doda', 'Ganderbal',
      'Jammu', 'Kathua', 'Kishtwar', 'Kulgam', 'Kupwara', 'Poonch', 'Pulwama',
      'Rajouri', 'Ramban', 'Reasi', 'Samba', 'Shopian', 'Srinagar', 'Udhampur',
    ],
  },
  {
    name: 'Ladakh',
    code: 'LA',
    districts: ['Kargil', 'Leh'],
  },
  {
    name: 'Lakshadweep',
    code: 'LD',
    districts: ['Lakshadweep'],
    extraCities: ['Kavaratti', 'Agatti', 'Minicoy'],
  },
  {
    name: 'Puducherry',
    code: 'PY',
    districts: ['Karaikal', 'Mahe', 'Puducherry', 'Yanam'],
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** All state names (alphabetical) — feed straight into an Autocomplete. */
export const STATES: string[] = STATES_DATA.map((s) => s.name).sort((a, b) =>
  a.localeCompare(b)
);

const stateIndex: Record<string, StateData> = STATES_DATA.reduce(
  (acc, s) => ({ ...acc, [s.name.toLowerCase()]: s }),
  {} as Record<string, StateData>
);

/** Districts of a state. Empty array if state is unknown / blank. */
export function districtsOf(stateName: string): string[] {
  if (!stateName) return [];
  const s = stateIndex[stateName.toLowerCase()];
  return s ? [...s.districts].sort((a, b) => a.localeCompare(b)) : [];
}

/**
 * Cities of a state — districts unioned with notable extra cities/towns.
 * Returned alphabetically with duplicates removed.
 */
export function citiesOf(stateName: string): string[] {
  if (!stateName) return [];
  const s = stateIndex[stateName.toLowerCase()];
  if (!s) return [];
  const set = new Set<string>([...s.districts, ...(s.extraCities ?? [])]);
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}
